import React, { FC, useContext } from 'react';
import moment from 'moment';
import { keyBy } from 'lodash';
import { Intl } from '@erecruit/core';
import {
  FormContext,
  FormContextType,
  FormItem,
  H1,
  Option,
  Select,
} from '@erecruit/ui-kit';
import {
  PositionTemplateFieldType,
  Resources,
  RequestFundingType,
  Mutations,
} from '@erecruit/types';
import DynamicFormItems from '../DynamicFormItems/DynamicFormItems';
import { Disclaimer } from '../Disclaimer';
import { WizardContext, PositionField } from '../../types';
import BillingForm from './components/BillingForm';
import { subtractEndDateFromStartDate } from '../ScheduleStep/helpers';
import { generateFieldOptions } from '../../helpers';

const { FormattedMessage, injectIntl } = Intl;

interface BillingStepForm {
  estimatedSpend: number;
  targetBillRate: number;
  minimumBillRate: number;
  maximumBillRate: number;
  requestFunding: RequestFundingType;
}

interface BillingStepProps {
  positionTemplateId: number;
  fields: PositionField[];
}

const BillingStep: FC<BillingStepProps & Intl.InjectedIntlProps> = props => {
  const { positionTemplateId, fields, intl } = props;
  const form = useContext<FormContextType<BillingStepForm>>(FormContext);

  const wizardContext = useContext(WizardContext);
  const {
    positionType,
    folderGroup,
    numberOfPositions = 0,
  } = wizardContext.general;
  const { worksiteAddress } = wizardContext.location;
  const {
    shiftTypeId,
    estimatedEndDate,
    estimatedStartDate,
    startTime,
    endTime,
  } = wizardContext.schedule;
  const { targetBillRate: initialTargetBillRate } = wizardContext.billing;

  const durationAmount = subtractEndDateFromStartDate(
    moment(estimatedStartDate),
    moment(estimatedEndDate),
  );

  const billingRateParams: Resources.PositionTemplateBillRatesParams = {
    positionTemplateId,
    positionTypeId: positionType,
    worksiteId: worksiteAddress,
    folderGroupId:
      folderGroup && folderGroup !== '' ? Number(folderGroup) : undefined,
    shiftTypeIds: shiftTypeId ? [shiftTypeId] : [],
  };

  const keyFields = keyBy(fields, 'fieldType');
  const requestFundingField =
    keyFields[PositionTemplateFieldType.RequestFunding];

  return (
    <>
      <H1>
        <FormattedMessage id={messageIds.vms.newPositionWizard.billing.title} />
      </H1>
      <Disclaimer>
        <FormattedMessage
          id={messageIds.vms.newPositionWizard.billing.disclaimer}
        />
      </Disclaimer>
      <BillingForm
        form={form}
        startTime={startTime}
        endTime={endTime}
        numberOfPositions={numberOfPositions}
        durationInDays={durationAmount}
        initialTargetBillRate={initialTargetBillRate}
        billingRateParams={billingRateParams}
      />
      {requestFundingField && (
        <FormItem
          id={nameof<Mutations.NewPositionParams>(o => o.requestFunding)}
          label={intl.formatMessage({
            id: messageIds.vms.newPositionWizard.billing.requestFunding,
          })}
          hasFeedback
          options={generateFieldOptions(
            requestFundingField,
            requestFundingField.value,
          )}
        >
          <Select
            disabled={
              requestFundingField.settings &&
              requestFundingField.settings.locked
            }
          >
            <Option key="base" value={RequestFundingType.Base}>
              <FormattedMessage id={messageIds.vms.general.base} />
            </Option>
            <Option key="project" value={RequestFundingType.Project}>
              <FormattedMessage id={messageIds.vms.general.project} />
            </Option>
          </Select>
        </FormItem>
      )}

      <DynamicFormItems section="billing" fields={fields} />
    </>
  );
};

export default injectIntl(BillingStep);
